import { logSerialize } from './debug-events';
import type { SerializedBody, Serializer } from './types';

/**
 * A body ready for the transport, plus the content-type it implies.
 *
 * `body` is `BodyInit` and not `SerializedBody`, because the two answer
 * different questions. `SerializedBody` is what a caller or a serializer may
 * hand IN, and its stream member is widened so a node backend can forward a
 * request body. This is what the transport RECEIVES, where a stream has to be
 * one for real.
 */
export interface WireBody {
  body?: BodyInit;
  /** Only what the body TYPE implies. An explicit header always wins over it. */
  contentType?: string;
}

const OCTET_STREAM = 'application/octet-stream';

/**
 * The realm-safe form of `instanceof`.
 *
 * `instanceof` asks which CONSTRUCTOR made a value, and that constructor is a
 * different object in every realm — two copies of one package in a monorepo are
 * two realms. The internal tag travels with the value itself, so it answers the
 * same in every realm (BA34, BA35).
 *
 * `Symbol.toStringTag` lets an object claim a tag it has no right to. That is no
 * weaker than `instanceof`, which `Symbol.hasInstance` bends just as easily. The
 * realm boundary is the ACCIDENTAL case, and it is the one that happens.
 */
function tagOf(value: unknown): string {
  return Object.prototype.toString.call(value);
}

function isURLSearchParams(value: unknown): value is URLSearchParams {
  return tagOf(value) === '[object URLSearchParams]';
}

function isFormData(value: unknown): value is FormData {
  return tagOf(value) === '[object FormData]';
}

/**
 * Two tags, because a `File` is a `Blob` that reports a tag of its own. A
 * subclass written here inherits `[object Blob]` and needs no entry; `File` is
 * the platform's own exception, not the first of a series.
 */
function isBlob(value: unknown): value is Blob {
  const tag = tagOf(value);
  return tag === '[object Blob]' || tag === '[object File]';
}

function isArrayBuffer(value: unknown): value is ArrayBuffer {
  return tagOf(value) === '[object ArrayBuffer]';
}

/**
 * A view whose memory is an ordinary `ArrayBuffer`, asked through the buffer's
 * internal tag like every other branch. A view over a `SharedArrayBuffer` is
 * not one: `fetch` refuses it, and TypeScript 5.9 says so in `BodyInit`.
 */
function isArrayBufferView(
  value: unknown,
): value is ArrayBufferView & { buffer: ArrayBuffer } {
  return ArrayBuffer.isView(value) && isArrayBuffer(value.buffer);
}

function isReadableStream(value: unknown): value is ReadableStream<Uint8Array> {
  return tagOf(value) === '[object ReadableStream]';
}

/**
 * Whether the value carries the tag symbol rather than inheriting its tag from
 * `Object.prototype`. That is what separates the platform body objects this
 * axis knows — `Blob` and `File`, `ArrayBuffer` and its views, `FormData`,
 * `URLSearchParams`, `ReadableStream` — from the JSON shapes `{}`, `[]`,
 * `null`, `0` and `false`, which carry nothing and report their tag from the
 * prototype. An object may define the symbol itself, so this separates the two
 * populations without proving membership of either.
 */
function hasToStringTag(value: unknown): boolean {
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    Symbol.toStringTag in value
  );
}

/**
 * Picks the transformation on the TYPE of the body, never on the method.
 *
 * Three groups, and the difference between them is who labels the content:
 * we label what we serialize ourselves (JSON) and what carries no label of its
 * own (bytes), and we stay out of the way for the shapes `fetch` labels — a
 * string, `URLSearchParams` and `FormData`, whose boundary cannot be produced
 * up front anyway.
 *
 * Every branch asks for the internal tag, a view included: `isArrayBufferView`
 * asks the tag of the view's buffer, which is what keeps a view over a
 * `SharedArrayBuffer` out as well. Asking the same way everywhere is what keeps
 * a foreign `ArrayBuffer` out of the JSON branch, but it does not close the
 * axis on its own: a platform type may announce a tag of its own, and `File`
 * does, so a `File` answers `[object File]` while being a `Blob`.
 *
 * That is why the JSON branch is no longer the catch-all it reads as. A value
 * carrying `Symbol.toStringTag` is refused here rather than handed to
 * `JSON.stringify`, which answers `{}` for a body it cannot see into and sends
 * an upload out empty. A failure is the better answer, because the body was
 * never written either way and only one of the two says so.
 *
 * The price is paid by an ordinary object that defines the symbol itself: its
 * fields used to serialize intact and it now fails. Nothing catches that at
 * compile time, because the body type constrains string keys and a symbol key
 * falls outside the index signature. Such an object has to be given a
 * serializer, which replaces this axis outright.
 *
 * Only `undefined` means "no body". `null`, `0` and `false` are JSON values.
 *
 * Throws a `TypeError` for a body this axis cannot write, including the one
 * `JSON.stringify` raises on a cyclic object or a BigInt; the caller wraps it.
 */
export function serializeBody(value: unknown): WireBody {
  if (value === undefined) return {};
  if (typeof value === 'string') return { body: value };
  if (isURLSearchParams(value) || isFormData(value)) return { body: value };
  if (isBlob(value))
    return {
      body: value,
      contentType: value.type === '' ? OCTET_STREAM : value.type,
    };
  if (
    isArrayBuffer(value) ||
    isArrayBufferView(value) ||
    isReadableStream(value)
  ) {
    return { body: value, contentType: OCTET_STREAM };
  }
  if (hasToStringTag(value)) {
    throw new TypeError(
      `A body of ${tagOf(
        value,
      )} is not a JSON value and no branch of the type axis writes it.`,
    );
  }
  return { body: JSON.stringify(value), contentType: 'application/json' };
}

/**
 * A serializer's answer as the transport can take it.
 *
 * `SerializedBody` is wider than `BodyInit` in exactly one place: its stream
 * member names only what reading bytes needs, so that a forwarded node stream is
 * spelled the same as a browser one. A value meeting that shape without carrying
 * the stream tag therefore satisfies the type and is still a body no branch of
 * this axis can write. A serializer is caller code, so a value that satisfies
 * nothing at all arrives here too.
 *
 * Each member is asked for by the same realm-safe tag the built-in
 * transformation uses, rather than being waved through on its static type. That
 * is the point: the type is the thing that has been widened, so it is no longer
 * the thing that can answer this.
 *
 * Throws a `TypeError` for a value neither branch matches; the caller wraps it,
 * exactly as it does for a body the built-in transformation refuses.
 */
function toWireValue(value: SerializedBody): BodyInit {
  if (
    typeof value === 'string' ||
    isURLSearchParams(value) ||
    isFormData(value) ||
    isBlob(value) ||
    isArrayBuffer(value) ||
    isArrayBufferView(value) ||
    isReadableStream(value)
  ) {
    return value;
  }
  throw new TypeError(
    `A serializer returned ${tagOf(
      value,
    )}, which is not a body the transport can write.`,
  );
}

/**
 * The body as it goes on the wire, by whichever of the two routes applies.
 *
 * A `serializer` REPLACES the type axis rather than extending it (SE2, SE9):
 * with one present `serializeBody` is not consulted at all, so an object body
 * does not quietly become JSON behind a serializer that returned something else.
 *
 * `serialize` may be async, and the promise is awaited here rather than handed
 * to the transport (SE5). It sits inside the call's deadline, so a serializer
 * that never settles ends the call as a timeout with nothing sent (SE14).
 */
export async function toWireBody(
  value: unknown,
  serializer: Serializer | undefined,
): Promise<WireBody> {
  const wireBody: WireBody =
    serializer === undefined
      ? serializeBody(value)
      : {
          body: toWireValue(await serializer.serialize(value)),
          contentType: serializer.contentType,
        };
  logSerialize(serializer, wireBody.body, wireBody.contentType);
  return wireBody;
}
